import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
    AlertTriangle,
    ArrowRight,
    Building2,
    Eye,
    Grid2x2,
    Heart,
    MapPin,
    Package,
    Shield,
    ShoppingBag,
    Sparkles,
    Star,
    Tag
} from 'lucide-react';
import { clubsService, marketplaceService } from '../../../services';
import StateCard from '../../components/feedback/StateCard';
import { getClubPublicPath } from '../../../utils/clubPublicPaths';
import './VenueStorefrontContent.css';

const CATEGORY_STYLES = {
    Shotgun: { emoji: 'S', tone: 'warm' },
    Rifle: { emoji: 'R', tone: 'slate' },
    Ammunition: { emoji: 'A', tone: 'ember' },
    Optics: { emoji: 'O', tone: 'sky' },
    Accessories: { emoji: 'X', tone: 'forest' },
    Clothing: { emoji: 'C', tone: 'berry' },
    Cases: { emoji: 'K', tone: 'sand' },
    Cleaning: { emoji: 'L', tone: 'teal' },
    Targets: { emoji: 'T', tone: 'gold' }
};

const DEFAULT_PRIMARY = '#12343b';
const DEFAULT_SECONDARY = '#dd6b20';

const formatPrice = (price) => new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
}).format(Number(price || 0));

const formatCompact = (value) => new Intl.NumberFormat('en-GB', {
    notation: 'compact',
    maximumFractionDigits: 1
}).format(Number(value || 0));

const normaliseUrl = (url) => {
    if (!url) {
        return null;
    }

    return url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
};

const getCategoryStyle = (category) => CATEGORY_STYLES[category] || { emoji: '#', tone: 'slate' };

const FREE_CATEGORY_LIMIT = 4;
const FREE_LISTING_LIMIT = 6;
const FREE_FEATURED_LIMIT = 2;
const FREE_PERK_LIMIT = 1;

const VenueStorefrontContent = ({ isPublic = false }) => {
    const { clubKey } = useParams();
    const navigate = useNavigate();
    const [club, setClub] = useState(null);
    const [listings, setListings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');

    useEffect(() => {
        const loadStorefront = async () => {
            if (!clubKey) {
                setError('Venue not found.');
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setError('');

            try {
                const clubData = await clubsService.getPublicClubProfile(clubKey);
                const listingData = await marketplaceService.getClubListings(clubData.clubId, 24);

                setClub(clubData);
                setListings(Array.isArray(listingData) ? listingData : []);
            } catch (loadError) {
                console.error('Failed to load venue storefront:', loadError);
                setError(loadError?.message || 'Unable to load this venue storefront right now.');
                setClub(null);
                setListings([]);
            } finally {
                setIsLoading(false);
            }
        };

        loadStorefront();
    }, [clubKey]);

    useEffect(() => {
        if (!isPublic || !club?.publicKey || !clubKey || clubKey === club.publicKey) {
            return;
        }

        navigate(getClubPublicPath(club), { replace: true });
    }, [club, clubKey, isPublic, navigate]);

    const isPremiumVenue = Boolean(
        club?.isPremiumVenue || (club?.profileTier || '').toLowerCase() === 'premium'
    );

    const categorySummary = useMemo(() => {
        const counts = listings.reduce((accumulator, listing) => {
            const key = listing.category || 'Other';
            accumulator[key] = (accumulator[key] || 0) + 1;
            return accumulator;
        }, {});

        return Object.entries(counts)
            .map(([category, count]) => ({ category, count, ...getCategoryStyle(category) }))
            .sort((left, right) => right.count - left.count);
    }, [listings]);

    const filteredListings = useMemo(() => {
        if (!selectedCategory) {
            return listings;
        }

        return listings.filter((listing) => listing.category === selectedCategory);
    }, [listings, selectedCategory]);

    const visibleCategorySummary = isPremiumVenue ? categorySummary : categorySummary.slice(0, FREE_CATEGORY_LIMIT);
    const visibleListings = isPremiumVenue ? filteredListings : filteredListings.slice(0, FREE_LISTING_LIMIT);
    const featuredListings = (isPremiumVenue ? filteredListings : visibleListings).slice(0, isPremiumVenue ? 3 : FREE_FEATURED_LIMIT);
    const spotlightOffers = (club?.offers || [])
        .filter((offer) => offer.isPublic !== false)
        .slice(0, isPremiumVenue ? 3 : FREE_PERK_LIMIT);
    const heroImage = club?.photos?.[0]?.photoUrl || club?.logoUrl || null;
    const primaryBrandColor = club?.primaryBrandColor || DEFAULT_PRIMARY;
    const secondaryBrandColor = club?.secondaryBrandColor || DEFAULT_SECONDARY;
    const websiteUrl = normaliseUrl(club?.website);
    const profilePath = isPublic ? getClubPublicPath(club) : `/dashboard/clubs/${club?.clubId}`;

    const stats = useMemo(() => ({
        liveListings: listings.length,
        categories: categorySummary.length,
        favourites: listings.reduce((sum, listing) => sum + Number(listing.favouriteCount || 0), 0),
        views: listings.reduce((sum, listing) => sum + Number(listing.viewCount || 0), 0)
    }), [categorySummary.length, listings]);

    const openListing = (listingId) => {
        navigate(`/dashboard/marketplace/${listingId}`);
    };

    if (isLoading) {
        return (
            <div className="venue-storefront-shell">
                <StateCard
                    icon={ShoppingBag}
                    title="Loading venue storefront"
                    description="Pulling in branding, category shelves, and live listings."
                    loading
                />
            </div>
        );
    }

    if (error || !club) {
        return (
            <div className="venue-storefront-shell">
                <StateCard
                    icon={AlertTriangle}
                    title="Venue storefront unavailable"
                    description={error || 'This venue storefront could not be loaded.'}
                    tone="error"
                />
            </div>
        );
    }

    return (
        <div
            className={`venue-storefront-shell ${isPublic ? 'public' : 'dashboard'} ${isPremiumVenue ? 'premium' : 'free'}`}
            style={{
                '--venue-primary': primaryBrandColor,
                '--venue-secondary': secondaryBrandColor
            }}
        >
            <section className="venue-storefront-hero">
                <div className="venue-storefront-hero-copy">
                    <div className="venue-storefront-kicker">
                        <span>{isPremiumVenue ? 'Premium venue storefront' : 'Venue marketplace presence'}</span>
                        {isPremiumVenue ? <Sparkles size={14} /> : <Building2 size={14} />}
                    </div>
                    <h1>{club.name} shop front</h1>
                    <p>
                        {isPremiumVenue
                            ? 'A branded venue storefront for browsing live inventory by category, with the venue profile and current perks all in one place.'
                            : 'This venue is selling through Shoot Connect marketplace. Free venues get a lighter listing preview, while premium venues unlock the fuller merchandised shopfront experience.'}
                    </p>

                    <div className="venue-storefront-hero-actions">
                        <Link to={profilePath} className="venue-storefront-btn primary">
                            View venue profile
                        </Link>
                        <Link to="/dashboard/marketplace" className="venue-storefront-btn secondary">
                            Browse marketplace
                        </Link>
                        {websiteUrl ? (
                            <a href={websiteUrl} className="venue-storefront-btn tertiary" target="_blank" rel="noreferrer">
                                Venue website
                            </a>
                        ) : null}
                    </div>

                    <div className="venue-storefront-stats">
                        <article>
                            <strong>{isPremiumVenue ? stats.liveListings : Math.min(stats.liveListings, FREE_LISTING_LIMIT)}</strong>
                            <span>{isPremiumVenue ? 'Live listings' : 'Previewed listings'}</span>
                        </article>
                        <article>
                            <strong>{isPremiumVenue ? stats.categories : Math.min(stats.categories, FREE_CATEGORY_LIMIT)}</strong>
                            <span>{isPremiumVenue ? 'Categories' : 'Preview categories'}</span>
                        </article>
                        <article>
                            <strong>{formatCompact(stats.views)}</strong>
                            <span>Total views</span>
                        </article>
                        <article>
                            <strong>{formatCompact(stats.favourites)}</strong>
                            <span>Favourites</span>
                        </article>
                    </div>
                </div>

                <div className="venue-storefront-hero-media">
                    {heroImage ? (
                        <img src={heroImage} alt={club.name} className="venue-storefront-hero-image" />
                    ) : (
                        <div className="venue-storefront-hero-placeholder">
                            {club.logoUrl ? <img src={club.logoUrl} alt={`${club.name} logo`} /> : <Building2 size={44} />}
                        </div>
                    )}

                    <div className="venue-storefront-hero-overlay">
                        <div>
                            <span>Location</span>
                            <strong>{club.address || 'Venue address coming soon'}</strong>
                        </div>
                        <div>
                            <span>Marketplace type</span>
                            <strong>{isPremiumVenue ? 'Branded premium storefront' : 'Standard listing view'}</strong>
                        </div>
                    </div>
                </div>
            </section>

            {!isPremiumVenue ? (
                <section className="venue-storefront-upgrade">
                    <Star size={18} />
                    <div>
                        <strong>Premium storefront features are admin-enabled</strong>
                        <p>
                            Free venues still show live marketplace inventory, but premium unlocks the fuller category-led shopfront,
                            stronger branding treatment, more promotional space, and richer perk visibility.
                        </p>
                    </div>
                </section>
            ) : null}

            <section className="venue-storefront-category-band">
                <div className="venue-storefront-section-heading">
                    <div>
                        <span>{isPremiumVenue ? 'Shop by category' : 'Browse listing types'}</span>
                        <h2>{isPremiumVenue ? 'Category browsing' : 'Lightweight category preview'}</h2>
                    </div>
                    <button
                        type="button"
                        className={`venue-storefront-chip ${selectedCategory ? '' : 'active'}`}
                        onClick={() => setSelectedCategory('')}
                    >
                        All items
                    </button>
                </div>

                {!isPremiumVenue ? (
                    <p className="venue-storefront-tier-note">
                        Free venue pages show a tighter preview of marketplace categories. Premium venues unlock the full shelf layout and broader merchandising treatment.
                    </p>
                ) : null}

                <div className="venue-storefront-category-grid">
                    {visibleCategorySummary.length === 0 ? (
                        <div className="venue-storefront-empty-panel">
                            <Package size={20} />
                            <p>No live category shelves yet. Listings will appear here when the venue publishes inventory.</p>
                        </div>
                    ) : (
                        visibleCategorySummary.map((category) => (
                            <button
                                type="button"
                                key={category.category}
                                className={`venue-storefront-category-card tone-${category.tone} ${selectedCategory === category.category ? 'selected' : ''}`}
                                onClick={() => setSelectedCategory(category.category)}
                            >
                                <span className="venue-storefront-category-icon">{category.emoji}</span>
                                <strong>{category.category}</strong>
                                <em>{category.count} live</em>
                            </button>
                        ))
                    )}
                </div>
            </section>

            <section className="venue-storefront-main-grid">
                <div className="venue-storefront-main-column">
                    <div className="venue-storefront-section-heading">
                        <div>
                            <span>{selectedCategory || (isPremiumVenue ? 'Featured inventory' : 'Listing preview')}</span>
                            <h2>{isPremiumVenue ? 'Shop front highlights' : 'Featured listing preview'}</h2>
                        </div>
                        <div className="venue-storefront-inline-meta">
                            <Grid2x2 size={15} />
                            <span>{isPremiumVenue ? filteredListings.length : visibleListings.length} items ready to browse</span>
                        </div>
                    </div>

                    {featuredListings.length === 0 ? (
                        <StateCard
                            icon={Package}
                            title="No listings in this category"
                            description="Try another category or return to all items."
                            actionLabel="Show all items"
                            actionOnClick={() => setSelectedCategory('')}
                        />
                    ) : (
                        <div className="venue-storefront-feature-grid">
                            {featuredListings.map((listing) => (
                                <article
                                    key={listing.listingId}
                                    className="venue-storefront-feature-card"
                                    onClick={() => openListing(listing.listingId)}
                                >
                                    <div className="venue-storefront-feature-image">
                                        {listing.firstImageUrl ? (
                                            <img src={listing.firstImageUrl} alt={listing.title} />
                                        ) : (
                                            <div className="venue-storefront-feature-placeholder">
                                                <Package size={30} />
                                            </div>
                                        )}
                                        <span className="venue-storefront-feature-tag">{listing.category}</span>
                                    </div>
                                    <div className="venue-storefront-feature-copy">
                                        <h3>{listing.title}</h3>
                                        <p>{[listing.make, listing.model].filter(Boolean).join(' ') || 'Venue listed item'}</p>
                                        <div className="venue-storefront-feature-meta">
                                            <strong>{formatPrice(listing.price)}</strong>
                                            {listing.location ? (
                                                <span><MapPin size={12} /> {listing.location}</span>
                                            ) : null}
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}

                    <div className="venue-storefront-section-heading inventory-heading">
                        <div>
                            <span>{isPremiumVenue ? 'Full inventory' : 'Marketplace preview'}</span>
                            <h2>{isPremiumVenue ? 'All live listings' : 'Preview listings'}</h2>
                        </div>
                        <div className="venue-storefront-inline-meta">
                            <Tag size={15} />
                            <span>
                                {selectedCategory
                                    ? `${selectedCategory} only`
                                    : (isPremiumVenue ? 'All categories' : `Up to ${FREE_LISTING_LIMIT} items shown`)}
                            </span>
                        </div>
                    </div>

                    {visibleListings.length === 0 ? (
                        <div className="venue-storefront-empty-panel">
                            <Package size={20} />
                            <p>This venue has not published any live listings yet.</p>
                        </div>
                    ) : (
                        <div className="venue-storefront-listing-grid">
                            {visibleListings.map((listing) => (
                                <button
                                    type="button"
                                    key={listing.listingId}
                                    className="venue-storefront-listing-card"
                                    onClick={() => openListing(listing.listingId)}
                                >
                                    <div className="venue-storefront-listing-image">
                                        {listing.firstImageUrl ? (
                                            <img src={listing.firstImageUrl} alt={listing.title} />
                                        ) : (
                                            <div className="venue-storefront-listing-placeholder">
                                                <Package size={26} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="venue-storefront-listing-body">
                                        <span className="venue-storefront-listing-category">{listing.category}</span>
                                        <h3>{listing.title}</h3>
                                        <p>{[listing.make, listing.model, listing.condition].filter(Boolean).join(' • ') || 'Venue marketplace listing'}</p>
                                        <div className="venue-storefront-listing-footer">
                                            <strong>{formatPrice(listing.price)}</strong>
                                            <div>
                                                <span><Eye size={12} /> {listing.viewCount}</span>
                                                <span><Heart size={12} /> {listing.favouriteCount}</span>
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}

                    {!isPremiumVenue && filteredListings.length > FREE_LISTING_LIMIT ? (
                        <div className="venue-storefront-free-summary">
                            <strong>{filteredListings.length - FREE_LISTING_LIMIT} more items are live in this venue inventory.</strong>
                            <span>Premium shopfronts surface the wider catalogue more prominently on the public venue journey.</span>
                        </div>
                    ) : null}
                </div>

                <aside className="venue-storefront-side-column">
                    <section className="venue-storefront-panel">
                        <div className="venue-storefront-section-heading compact">
                            <div>
                                <span>About the venue</span>
                                <h2>Seller snapshot</h2>
                            </div>
                        </div>
                        <p>{club.description || 'This venue has not yet added a longer description for its store front.'}</p>
                        <div className="venue-storefront-facts">
                            <span><MapPin size={14} /> {club.address || 'Address pending'}</span>
                            <span><Building2 size={14} /> {isPremiumVenue ? 'Premium venue' : 'Free venue'}</span>
                            {club.hasShop ? <span><ShoppingBag size={14} /> On-site shop available</span> : null}
                        </div>
                    </section>

                    <section className="venue-storefront-panel">
                        <div className="venue-storefront-section-heading compact">
                            <div>
                                <span>Venue offers</span>
                                <h2>{isPremiumVenue ? 'Current perks' : 'Featured perk'}</h2>
                            </div>
                        </div>
                        {spotlightOffers.length === 0 ? (
                            <div className="venue-storefront-empty-panel small">
                                <Sparkles size={18} />
                                <p>No public perks are featured right now.</p>
                            </div>
                        ) : (
                            <div className="venue-storefront-perk-list">
                                {spotlightOffers.map((offer) => (
                                    <article key={offer.clubOfferId} className="venue-storefront-perk-card">
                                        <strong>{offer.title}</strong>
                                        <p>{offer.description || 'Venue offer now live on Shoot Connect.'}</p>
                                        {offer.offerCode ? <span>Code: {offer.offerCode}</span> : null}
                                    </article>
                                ))}
                            </div>
                        )}
                    </section>

                    <section className="venue-storefront-panel legal">
                        <div className="venue-storefront-section-heading compact">
                            <div>
                                <span>Safe trading</span>
                                <h2>Marketplace notes</h2>
                            </div>
                        </div>
                        <ul>
                            <li>Firearms and ammunition must comply with UK law and certificate checks.</li>
                            <li>Listings marked collection-only must be completed in person.</li>
                            <li>Shoot Connect facilitates discovery and buyer conversations, not transfer handling.</li>
                        </ul>
                        <div className="venue-storefront-legal-badge">
                            <Shield size={15} />
                            <span>Safe, compliant, venue-led selling</span>
                        </div>
                    </section>
                </aside>
            </section>

            <section className="venue-storefront-footer-cta">
                <div>
                    <span>{isPremiumVenue ? 'Want the full venue context?' : 'Need the full venue context?'}</span>
                    <h2>{isPremiumVenue ? 'See the wider venue profile' : 'Open the main venue profile'}</h2>
                    <p>
                        {isPremiumVenue
                            ? 'Open the venue page for events, facilities, gallery images, and profile information beyond the storefront.'
                            : 'Open the venue page for the broader venue profile, facilities, and the lighter free-profile experience outside this listing preview.'}
                    </p>
                </div>
                <Link to={profilePath} className="venue-storefront-btn primary">
                    Open venue profile
                    <ArrowRight size={16} />
                </Link>
            </section>
        </div>
    );
};

export default VenueStorefrontContent;
